import React, { useEffect, useState } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./customTable.css";
import commoncss from "../../../myfin_v2/css/common.module.css";
import MenuIcon from "../../../myfin_v2/assets/icons/Menu.png";
import { ReactComponent as PlusIcon } from "../../../myfin_v2/assets/icons/plusprimary.svg";

const MERGE_KEYS = ["date", "particular", "PARTICULAR", "DATE"];

const genObjectId = () =>
  Array.from({ length: 24 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join("");

const isPrimitive = (v) =>
  v === null || ["string", "number", "boolean"].includes(typeof v);

const buildRowSpanMap = (rows, mergeKeys) => {
  const spanMap = {};
  let i = 0;

  while (i < rows.length) {
    let span = 1;

    for (
      let j = i + 1;
      j < rows.length && mergeKeys.every((k) => rows[j][k] === rows[i][k]);
      j++
    ) {
      span++;
    }

    mergeKeys.forEach((key) => {
      spanMap[`${i}-${key}`] = span;
      for (let k = i + 1; k < i + span; k++) {
        spanMap[`${k}-${key}`] = 0;
      }
    });

    i += span;
  }

  return spanMap;
};

const getStickyStyle = (
  col,
  isHeader,
  extraStyle = {},
  isPrePopulate = false,
  isLastSimpleCol = false,
  isFirstCol = false,
  prePopulateCashflowUI = false,
  tableType = "",
  isMainHeader = false
) => {
  const base = {};
  if (isPrePopulate && isHeader) {
    if (isMainHeader) {
      base.verticalAlign = "top";
      base.borderBottom = "1px solid #ccd5d3";
      if (isLastSimpleCol) {
        base.borderRight = "1px solid #ccd5d3";
      }
    } else {
      base.verticalAlign = "middle";
      base.borderBottom = "1px solid #ccd5d3";
      if (isLastSimpleCol) {
        base.borderRight = "1px solid #ccd5d3";
      }
    }
  }

  const colLower = String(col).trim().toLowerCase();

  if (isPrePopulate && tableType === "cashflow" && (colLower === "particular" || isFirstCol)) {
    base.borderRight = "none";
  }

  if (colLower === "particular" || isFirstCol) {
    return {
      ...base,
      position: "sticky",
      left: 0,
      zIndex: isHeader ? 100 : 30,
      background: isHeader ? "#eaeeed" : "#fff",
      paddingLeft: prePopulateCashflowUI && !isHeader ? "28px" : "24px",
      ...extraStyle,
    };
  }
  if (colLower === "basecurrentyear") {
    return {
      ...base,
      position: "sticky",
      left: "180px",
      zIndex: isHeader ? 100 : 3,
      background: isHeader ? "#eaeeed" : "#fff",
      textAlign: "right",
      width: "170px",
      minWidth: "100px",
      ...extraStyle,
    };
  }
  return { ...base, ...extraStyle };
};

const formatCellNumber = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const num = Math.round(Number(val));
  if (isNaN(num)) return String(val);
  if (num < 0) {
    return `(${Math.abs(num).toLocaleString("en-IN")})`;
  }
  return num.toLocaleString("en-IN");
};

const displaySimpleValue = (row, col) => {
  const v = row[col];
  if (v != null && v !== "") return String(v);
  if (row.isNewRow) return "Enter text";
  return "";
};

const displayCompanyValue = (company, period, row) => {
  const v = company[period];
  if (v !== null && v !== undefined && v !== "") {
    return formatCellNumber(v);
  }
  if (row.isNewRow) return "0";
  return "";
};

const ConsoleTable = ({
  tableData,
  tableTitle = null,
  type,
  id,
  isPrePopulate = false,
  allowDragAndAdd = false,
  prePopulateCashflowUI = false,
  onRowsChange = () => {},
}) => {
  const cashflowUI =
    prePopulateCashflowUI || (allowDragAndAdd && isPrePopulate && type === "cashflow");
  const [localRows, setLocalRows] = useState(
    Array.isArray(tableData) ? [...tableData] : []
  );

  useEffect(() => {
    setLocalRows(Array.isArray(tableData) ? [...tableData] : []);
  }, [tableData]);

  const [dndReady, setDndReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setDndReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  if (!localRows?.length) return null;

  const isNumericColumn = (col) => {
    const colLower = String(col).trim().toLowerCase();
    if (colLower === "particular" || colLower === "date") return false;
    return localRows.some((row) => {
      const val = row[col];
      if (val === null || val === undefined || val === "") return false;
      return typeof val === "number" || (!isNaN(val) && !isNaN(parseFloat(val)));
    });
  };

  const simpleColumns = Object.keys(localRows[0]).filter(
    (key) =>
      !key.startsWith("_") &&
      key !== "companies" &&
      key !== "lineType" &&
      key !== "currentYear" &&
      key !== "previousYear" &&
      key !== "baseCurrentYear" &&
      key !== "basePreviousYear" &&
      key !== "isNewRow" &&
      isPrimitive(localRows[0][key])
  );

  const companies = localRows[0].companies || [];

  const periods = Array.from(
    new Set(
      companies.flatMap((company) =>
        Object.keys(company).filter((k) => k !== "title" && !k.startsWith("_"))
      )
    )
  );

  const rowSpanMap = buildRowSpanMap(localRows, MERGE_KEYS);
  const normalize = (str) =>
    String(str || "").replace(/[^a-z0-9]/gi, "").toLowerCase();

  const isNotesView =
    isPrePopulate && type && ["BS notes", "PL notes"].includes(type);
  const isNotesOrDisclosure =
    isPrePopulate &&
    type &&
    ["BS notes", "PL notes", "disclosure"].includes(type);
  const isCashflowScrollView =
    isPrePopulate && type === "cashflow" && cashflowUI;

  const droppableId = `console-table-rows-${id || type || "default"}`;

  const isHeaderRow = (row) =>
    (row?.lineType || "").toLowerCase() === "header";

  const mergeReorderedDataRows = (dataRows) => {
    let dataIdx = 0;
    return localRows.map((row) =>
      isHeaderRow(row) ? row : dataRows[dataIdx++]
    );
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const dataRows = localRows.filter((row) => !isHeaderRow(row));
    const items = Array.from(dataRows);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    const merged = mergeReorderedDataRows(items);
    setLocalRows(merged);
    try {
      onRowsChange(merged);
    } catch (e) {
      console.error("onRowsChange error", e);
    }
  };

  const buildEmptyCompanies = () =>
    companies.map((company) => {
      const entry = { title: company.title };
      periods.forEach((p) => {
        entry[p] = "";
      });
      return entry;
    });

  const addNewRow = () => {
    const newRow = {
      _id: genObjectId(),
      isNewRow: true,
      lineType: "row",
      companies: buildEmptyCompanies(),
    };
    simpleColumns.forEach((col) => {
      newRow[col] = "";
    });
    setLocalRows([...localRows, newRow]);
  };

  const totalCols =
    simpleColumns.length + companies.length * periods.length;

  const renderRowCells = (row, rowIndex, dragProvided = null) => {
    const isHeader = isHeaderRow(row);

    return (
      <>
        {simpleColumns.map((col, colIdx) => {
          const colLower = String(col).trim().toLowerCase();
          const isFirstCol = colIdx === 0;
          const isStickyCol = colLower === "particular" || isFirstCol;
          const isNumeric = isNumericColumn(col);

          const tdClass = isStickyCol
            ? "col-text"
            : isNumeric
            ? "col-number"
            : "col-text-nonsticky";

          const alignStyle = {
            textAlign: isStickyCol ? "left" : isNumeric ? "right" : "left",
          };

          const isEmptyPlaceholder =
            row.isNewRow && (row[col] == null || row[col] === "");
          const cellText = displaySimpleValue(row, col);

          if (MERGE_KEYS.includes(col) || MERGE_KEYS.includes(colLower)) {
            const span = rowSpanMap[`${rowIndex}-${col}`];
            if (span === 0) return null;

            return (
              <td
                key={col}
                rowSpan={span}
                className={`${tdClass} ${isHeader ? "header-cell" : ""}`}
                style={getStickyStyle(
                  col,
                  isHeader,
                  alignStyle,
                  isPrePopulate,
                  false,
                  isFirstCol,
                  cashflowUI,
                  type
                )}
              >
                {cashflowUI && isFirstCol && dragProvided && (
                  <div
                    {...dragProvided.dragHandleProps}
                    className="ft-row-drag-handle"
                    style={{
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      display: "flex",
                      alignItems: "center",
                    }}
                    title="Drag to reorder"
                  >
                    <img src={MenuIcon} alt="drag" style={{ width: 9, height: 13 }} />
                  </div>
                )}
                <span
                  style={{
                    color: isEmptyPlaceholder ? "#aaa" : "inherit",
                    fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                  }}
                >
                  {cellText}
                </span>
              </td>
            );
          }

          return (
            <td
              key={col}
              className={`${tdClass} ${isHeader ? "header-cell" : ""}`}
              style={{
                ...getStickyStyle(
                  col,
                  isHeader,
                  alignStyle,
                  isPrePopulate,
                  false,
                  isFirstCol,
                  cashflowUI,
                  type
                ),
                position: isFirstCol && cashflowUI ? "relative" : undefined,
              }}
            >
              {cashflowUI && isFirstCol && dragProvided && (
                <div
                  {...dragProvided.dragHandleProps}
                  className="ft-row-drag-handle"
                  style={{
                    position: "absolute",
                    left: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }}
                  title="Drag to reorder"
                >
                  <img src={MenuIcon} alt="drag" style={{ width: 9, height: 13 }} />
                </div>
              )}
              <span
                style={{
                  color: isEmptyPlaceholder ? "#aaa" : "inherit",
                  fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                }}
              >
                {cellText}
              </span>
            </td>
          );
        })}

        {(row.companies || []).map((company, cIdx) =>
          periods.map((period) => {
            const isEmptyPlaceholder =
              row.isNewRow &&
              (company[period] == null || company[period] === "");
            const cellText = displayCompanyValue(company, period, row);

            return (
              <td
                key={`${rowIndex}-${cIdx}-${period}`}
                className={`col-number ${isHeader ? "header-cell" : ""}`}
              >
                <span
                  style={{
                    color: isEmptyPlaceholder ? "#aaa" : "inherit",
                    fontStyle: isEmptyPlaceholder ? "italic" : "normal",
                  }}
                >
                  {cellText}
                </span>
              </td>
            );
          })
        )}
      </>
    );
  };

  const renderBodyRows = () => {
    if (!cashflowUI || !dndReady) {
      return localRows.map((row, rowIndex) => {
        const rowId = normalize(row.particular);
        return (
          <tr
            key={row._id || rowIndex}
            id={rowId}
            className={isHeaderRow(row) ? "row-header" : ""}
            style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined }}
          >
            {renderRowCells(row, rowIndex)}
          </tr>
        );
      });
    }

    let dragIndex = 0;
    return localRows.map((row, rowIndex) => {
      const rowKey = row._id ?? rowIndex;
      const rowId = normalize(row.particular);

      if (isHeaderRow(row)) {
        return (
          <tr key={rowKey} id={rowId} className="row-header" style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined }}>
            {renderRowCells(row, rowIndex)}
          </tr>
        );
      }

      const draggableId = `${droppableId}-${rowKey}`;
      const currentDragIndex = dragIndex++;

      return (
        <Draggable
          key={draggableId}
          draggableId={draggableId}
          index={currentDragIndex}
        >
          {(dragProvided) => (
            <tr
              ref={dragProvided.innerRef}
              {...dragProvided.draggableProps}
              id={rowId}
              style={{ fontWeight: (row.isBold === true || String(row.isBold).toLowerCase() === "true") ? 700 : undefined }}
            >
              {renderRowCells(row, rowIndex, dragProvided)}
            </tr>
          )}
        </Draggable>
      );
    });
  };

  const tableElement = (
    <table
      className="financial-table"
      style={{
        width: "100%",
        minWidth: "100%",
        borderCollapse: "separate",
      }}
    >
      <thead
        style={
          isPrePopulate
            ? { position: "sticky", top: 0, zIndex: 100, backgroundColor: "#eaeeed" }
            : undefined
        }
      >
        <tr>
          {simpleColumns.map((col, idx) => {
            const colLower = String(col).trim().toLowerCase();
            const isFirstCol = idx === 0;
            const isStickyCol = colLower === "particular" || isFirstCol;
            const isNumeric = isNumericColumn(col);

            const thClass = isStickyCol
              ? "col-text particular-heading"
              : isNumeric
              ? "col-number"
              : "col-text-nonsticky";

            const alignStyle = {
              textAlign: isStickyCol ? "left" : isNumeric ? "right" : "left",
            };

            return (
              <th
                key={col}
                rowSpan={isPrePopulate ? 1 : 2}
                className={`${thClass} tableHeadingColor`}
                style={getStickyStyle(
                  col,
                  true,
                  {
                    ...alignStyle,
                  },
                  isPrePopulate,
                  isPrePopulate && idx === simpleColumns.length - 1,
                  isFirstCol,
                  false,
                  type,
                  true
                )}
              >
                {col.toUpperCase()}
              </th>
            );
          })}

          {companies.map((company, idx) => (
            <th
              key={idx}
              className="col-group tableHeadingColor"
              colSpan={periods.length}
            >
              {company.title}
            </th>
          ))}
        </tr>

        <tr>
          {isPrePopulate && simpleColumns.map((col, idx) => {
            const isFirstCol = idx === 0;
            const isStickyCol = String(col).trim().toLowerCase() === "particular" || isFirstCol;
            return (
              <th
                key={`empty-${col}`}
                className={`${isStickyCol ? "col-text particular-heading" : "col-text-nonsticky"} tableHeadingColor`}
                style={getStickyStyle(
                  col,
                  true,
                  {},
                  isPrePopulate,
                  isPrePopulate && idx === simpleColumns.length - 1,
                  isFirstCol,
                  cashflowUI,
                  type,
                  false
                )}
              ></th>
            );
          })}
          {companies.map(() =>
            periods.map((p) => (
              <th
                key={p}
                className="col-number tableHeadingColor"
                style={cashflowUI && String(p).trim().toUpperCase() === "PREVIOUSYEAR" ? { borderRight: "none" } : undefined}
              >
                {p}
              </th>
            ))
          )}
        </tr>
      </thead>

      {cashflowUI && dndReady ? (
        <Droppable droppableId={droppableId} type="ROW">
          {(provided) => (
            <tbody ref={provided.innerRef} {...provided.droppableProps}>
              {renderBodyRows()}
              {provided.placeholder}
            </tbody>
          )}
        </Droppable>
      ) : (
        <tbody>{renderBodyRows()}</tbody>
      )}

      {cashflowUI && (
        <tfoot>
          <tr>
            <td colSpan={totalCols} style={{ border: "none", padding: "8px 0", }}>
              <button
                type="button"
                onClick={addNewRow}
                style={{
                  background: "none",
                  border: "none",
                  color: "#205a46",
                  cursor: "pointer",
                  fontWeight: "500",
                  padding: "8px 12px 8px 20px",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <PlusIcon style={{ width: 14, height: 14 }} /> Add Line Item
              </button>
            </td>
          </tr>
        </tfoot>
      )}
    </table>
  );

  return (
    <>
      <div
        className="table-outer"
        style={{
          width: "100%",
          marginTop: isNotesOrDisclosure
            ? ["BS notes", "PL notes"].includes(type)
              ? "5px"
              : "25px"
            : "0px",
          marginBottom: type === "disclosure" ? "25px" : "0px",
        }}
        id={id}
      >
        {tableTitle && (
          <div
            className="table-title-wrapper"
            style={
              isNotesView ? { borderRadius: "10px 10px 0 0" } : undefined
            }
          >
            <span
              className={`table-title ${commoncss.fs22} ${commoncss.fw500}`}
            >
              {tableTitle}
            </span>
          </div>
        )}
        <div
          className="table-container1"
          style={{
            width: "100%",
            border: "1px solid #ccd5d3",
            borderRadius: "10px",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: isPrePopulate
              ? isNotesView
                ? "calc(100vh - 95px)"
                : type === "cashflow"
                ? "calc(100vh - 95px)"
                : "calc(100vh - 95px)"
              : "calc(88vh - 60px)",
            borderTop: tableTitle ? "none" : "1px solid #ccd5d3",
            borderTopLeftRadius: tableTitle ? "0" : "10px",
            borderTopRightRadius: tableTitle ? "0" : "10px",
          }}
        >
          {cashflowUI ? (
            <DragDropContext onDragEnd={onDragEnd}>{tableElement}</DragDropContext>
          ) : (
            tableElement
          )}
        </div>
      </div>
    </>
  );
};

export default ConsoleTable;
